import { GoogleMap, Libraries, LoadScriptNext } from '@react-google-maps/api'
import { Button, Card, Col, Form, Row, Select, Space, Typography } from 'antd'
import { useEffect, useRef, useState } from 'react'

const { Text } = Typography

type Props = {
  setShowAddressForm: React.Dispatch<React.SetStateAction<boolean>>
  showAddressForm: boolean
}

const googleMapLibraries: Libraries = ['marker', 'places']

interface PlaceOption {
  value: string
  label: string
  placeId: string
}

export const VenueMap = ({ setShowAddressForm, showAddressForm }: Props) => {
  const {
    VITE_GOOGLE_MAPS_ID: GOOGLE_MAPS_ID,
    VITE_GOOGLE_MAPS_API_KEY: GOOGLE_MAPS_API_KEY,
  } = import.meta.env

  const form = Form.useFormInstance()
  const latitude = Form.useWatch(['address', 'latitude'], form)
  const longitude = Form.useWatch(['address', 'longitude'], form)

  const [center, setCenter] = useState({
    lat: Number(form.getFieldValue(['address', 'latitude']) ?? 0),
    lng: Number(form.getFieldValue(['address', 'longitude']) ?? 0),
  })

  const [placeOptions, setPlaceOptions] = useState<PlaceOption[]>([])
  const [searchLoading, setSearchLoading] = useState<boolean>(false)

  const mapRef = useRef<google.maps.Map | null>(null)
  const markerRef = useRef<google.maps.marker.AdvancedMarkerElement | null>(
    null
  )
  const placesLibRef = useRef<any>(null)

  const handleMapLoad = async (map: google.maps.Map) => {
    mapRef.current = map
    try {
      placesLibRef.current = await (google.maps as any).importLibrary('places')
    } catch (error) {
      console.error('Error loading Places API:', error)
    }
  }

  const addMarker = () => {
    const map = mapRef.current
    if (!map) {
      return
    }
    markerRef.current = new google.maps.marker.AdvancedMarkerElement({
      map,
      position: new google.maps.LatLng(center ?? [0, 0]),
      title: 'Position',
      gmpDraggable: true,
    })
    markerRef.current.addListener(
      'dragend',
      (event: google.maps.MapMouseEvent) => {
        if (!event.latLng) return

        const newPos = {
          lat: event.latLng.lat(),
          lng: event.latLng.lng(),
        }

        setCenter(newPos)

        form.setFieldsValue({
          address: {
            latitude: String(newPos.lat),
            longitude: String(newPos.lng),
          },
        })

        map.panTo(newPos)
      }
    )
  }

  const handlePlaceSearch = async (searchText: string) => {
    if (!searchText) {
      setPlaceOptions([])
      return
    }

    setSearchLoading(true)

    try {
      let options: PlaceOption[] = []

      if (placesLibRef.current) {
        try {
          const { Place } = placesLibRef.current
          const request = {
            textQuery: searchText,
            fields: ['displayName', 'formattedAddress', 'location', 'id'],
            locationBias: mapRef.current?.getBounds(),
            maxResultCount: 5,
          }

          const response = await Place.searchByText(request)
          const places = response.places || response.results || response

          if (places && Array.isArray(places) && places.length > 0) {
            options = places.map((place: any, index: number) => {
              const displayName =
                place.displayName || place.name || `Location ${index + 1}`
              const address =
                place.formattedAddress ||
                place.formatted_address ||
                'Address unavailable'
              const placeId = place.id || place.place_id || `place_${index}`

              return {
                value: placeId,
                label: `${displayName} - ${address}`,
                placeId: placeId,
              }
            })
          }
        } catch (placesError) {
          console.warn('Places API failed, trying Geocoding API:', placesError)
        }
      }

      if (options.length === 0) {
        const geocoder = new google.maps.Geocoder()

        const geocodeRequest: google.maps.GeocoderRequest = {
          address: searchText,
          bounds: mapRef.current?.getBounds(),
        }

        try {
          const response = await new Promise<google.maps.GeocoderResponse>(
            (resolve, reject) => {
              geocoder.geocode(geocodeRequest, (results, status) => {
                if (status === 'OK' && results) {
                  resolve({ results } as google.maps.GeocoderResponse)
                } else {
                  reject(new Error(`Geocoding failed: ${status}`))
                }
              })
            }
          )

          if (response.results && response.results.length > 0) {
            options = response.results.slice(0, 8).map((result, index) => ({
              value: result.place_id || `geocode_${index}`,
              label: result.formatted_address,
              placeId: result.place_id || `geocode_${index}`,
            }))
          }
        } catch (geocodeError) {
          console.error('Geocoding API failed:', geocodeError)
        }
      }

      setPlaceOptions(options)
    } catch (error) {
      console.error('General search error:', error)
      setPlaceOptions([])
    }

    setSearchLoading(false)
  }

  const handlePlaceSelect = async (value: string) => {
    if (!mapRef.current) return
    setSearchLoading(true)
    try {
      let placeData: any = null

      if (placesLibRef.current && !value.startsWith('geocode_')) {
        try {
          const { Place } = placesLibRef.current

          const place = new Place({
            id: value,
            requestedLanguage: 'en',
          })

          await place.fetchFields({
            fields: [
              'displayName',
              'formattedAddress',
              'location',
              'viewport',
              'addressComponents',
            ],
          })
          const postalCode = place.addressComponents?.find((component: any) =>
            component.types.includes('postal_code')
          )?.longText
          const streetNumber = place.addressComponents?.find((component: any) =>
            component.types.includes('street_number')
          )?.longText
          const street = place.addressComponents?.find((component: any) =>
            component.types.includes('route')
          )?.longText
          placeData = {
            location: place.location,
            viewport: place.viewport,
            name: place.displayName,
            address: streetNumber + ' ' + street,
            postCode: postalCode,
          }
        } catch (placesError) {
          console.warn(
            'Places API selection failed, trying to add manually',
            placesError
          )
        }
      }

      if (placeData?.location) {
        if (placeData.viewport) {
          mapRef.current.fitBounds(placeData.viewport)
        } else {
          mapRef.current.setCenter(placeData.location)
          mapRef.current.setZoom(17)
        }

        if (markerRef.current) {
          markerRef.current.position = placeData.location
        }

        const newPos = {
          lat:
            typeof placeData.location.lat === 'function'
              ? placeData.location.lat()
              : placeData.location.lat,
          lng:
            typeof placeData.location.lng === 'function'
              ? placeData.location.lng()
              : placeData.location.lng,
        }
        setCenter(newPos)
        form.setFieldsValue({
          address: {
            label: placeData.address,
            postCode: placeData.postCode,
            latitude: String(newPos.lat),
            longitude: String(newPos.lng),
          },
        })
        setShowAddressForm(true)
      }
    } catch (error) {
      console.error('Selection error:', error)
    }
    setSearchLoading(false)
  }

  useEffect(() => {
    if (!markerRef.current) {
      addMarker()
    }
    if (markerRef.current && latitude && longitude) {
      const newPos = new google.maps.LatLng(Number(latitude), Number(longitude))
      markerRef.current.position = newPos
      setCenter({ lat: newPos.lat(), lng: newPos.lng() })
      mapRef.current?.panTo(newPos)
    }
  }, [latitude, longitude, mapRef.current])

  return (
    <LoadScriptNext
      googleMapsApiKey={GOOGLE_MAPS_API_KEY}
      libraries={googleMapLibraries}
    >
      <Space orientation="vertical" className="w-full" size={10}>
        {!showAddressForm && (
          <Row gutter={10}>
            <Col span={18}>
              <Space orientation="vertical" size={8} className="w-full">
                <Text className="field-required">Address</Text>
                <Select
                  placeholder="Search for an address..."
                  style={{ width: '100%' }}
                  showSearch={{
                    filterOption: false,
                    onSearch: handlePlaceSearch,
                  }}
                  onSelect={handlePlaceSelect}
                  loading={searchLoading}
                  options={placeOptions}
                  notFoundContent={
                    searchLoading ? 'Searching...' : 'No results'
                  }
                />
              </Space>
            </Col>
            <Col span={6}>
              <Button
                style={{ marginTop: 30 }}
                onClick={() => setShowAddressForm(true)}
                block
              >
                Manual
              </Button>
            </Col>
          </Row>
        )}
        <Card
          styles={{ body: { padding: 10 } }}
          style={{ display: showAddressForm ? 'block' : 'none' }}
        >
          <Space orientation="vertical" className="w-full">
            <Text type="secondary">
              You can drag & drop the marker on the map to improve location
              accuracy.
            </Text>
            <GoogleMap
              mapContainerStyle={{
                width: '100%',
                height: '400px',
                borderRadius: 5,
              }}
              center={center}
              zoom={12}
              onLoad={handleMapLoad}
              options={{
                mapId: GOOGLE_MAPS_ID,
                streetViewControl: false,
                rotateControl: false,
                mapTypeControl: false,
                fullscreenControl: false,
              }}
            />
          </Space>
        </Card>
      </Space>
    </LoadScriptNext>
  )
}

export type VenueMapType = { Map: typeof VenueMap }
